import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, ArrowRight } from "lucide-react";
import api from "../lib/api";

// Displayed left to right: 2nd, 1st, 3rd (podium order)
const PRIZES = [
  { rank: 2, label: "2nd place", amount: 30, color: "#d4d4d8", height: 168 },
  { rank: 1, label: "1st place", amount: 50, color: "#facc15", height: 212 },
  { rank: 3, label: "3rd place", amount: 20, color: "#cd7f32", height: 144 },
];

const TICKS = [0, 0.25, 0.5, 0.75, 1];

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

function useCountUp(target, duration) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || window.__IS_PRERENDER__ || target <= 0) {
      setVal(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / (duration || 1400));
      setVal(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

export default function ChallengeTeaser({ members = 0, goal = 5000 }) {
  const safeGoal = Number(goal) > 0 ? Number(goal) : 5000;
  const [liveMembers, setLiveMembers] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/stats/members")
      .then((r) => {
        if (alive && r && r.data && typeof r.data.members === "number") {
          setLiveMembers(r.data.members);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const currentMembers = liveMembers !== null ? liveMembers : members;
  const safeMembers = Math.max(0, Math.min(Number(currentMembers) || 0, 1e9));
  const pct = Math.min(100, (safeMembers / safeGoal) * 100);
  const remaining = Math.max(0, safeGoal - safeMembers);
  const unlocked = safeMembers >= safeGoal;
  const shown = useCountUp(safeMembers, 1400);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setFilled(true), 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="max-w-[1680px] mx-auto px-6 md:px-10 py-10 md:py-14" aria-label="Monthly trading challenge">
      <style>{`
        @keyframes ct-shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        .ct-shimmer { animation: ct-shimmer 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .ct-shimmer { animation: none; } }
      `}</style>

      <div
        className="relative overflow-hidden border bg-[var(--bg-panel)]"
        style={{ borderColor: "rgba(250,204,21,0.30)", boxShadow: "0 0 60px -30px rgba(250,204,21,0.35)" }}
      >
        {/* decorative grid + glow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(var(--border-soft) 1px, transparent 1px), linear-gradient(90deg, var(--border-soft) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            opacity: 0.35,
            WebkitMaskImage: "radial-gradient(ellipse at 20% 0%, #000 0%, transparent 70%)",
            maskImage: "radial-gradient(ellipse at 20% 0%, #000 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(250,204,21,0.14) 0%, transparent 65%)" }}
        />

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 p-6 md:p-12">
          {/* LEFT: message + progress */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <span className="inline-flex items-center gap-2 self-start text-[10px] font-mono uppercase tracking-widest text-yellow-400 border border-yellow-400/30 px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
              {unlocked ? "Live now" : "Upcoming"} · Monthly Trading Challenge
            </span>

            <h2 className="mt-5 font-display font-bold text-3xl md:text-5xl leading-[1.08] tracking-tight text-[var(--text-primary)]">
              Trade <span className="text-yellow-400">$10,000</span> in virtual cash.
              <br className="hidden md:block" /> Win real USDT.
            </h2>

            <p className="mt-4 max-w-xl text-[var(--text-secondary)] leading-relaxed">
              Free to enter. Every month the top three traders on the leaderboard are paid in USDT.
              {unlocked
                ? " The prizes are unlocked."
                : " The prizes unlock when MediaCrypto reaches " + fmt(safeGoal) + " members."}
            </p>

            {/* counter */}
            <div className="mt-8">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex items-baseline gap-3">
                  <Users className="w-5 h-5 text-yellow-400 self-center" />
                  <span className="font-display font-bold text-4xl md:text-5xl text-[var(--text-primary)] tabular-nums">
                    {fmt(shown)}
                  </span>
                  <span className="font-mono text-sm text-[var(--text-tertiary)]">
                    / {fmt(safeGoal)} members
                  </span>
                </div>
                <span className="font-mono text-xs uppercase tracking-widest text-yellow-400">
                  {unlocked ? "Goal reached" : fmt(remaining) + " to unlock"}
                </span>
              </div>

              {/* progress bar */}
              <div
                className="relative mt-4 h-3 bg-[var(--bg-elevated)] border border-[var(--border-soft)] overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={safeGoal}
                aria-valuenow={Math.min(safeMembers, safeGoal)}
                aria-label="Members needed to unlock prizes"
              >
                <div
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{
                    width: filled ? pct + "%" : "0%",
                    transition: "width 1.4s cubic-bezier(0.22, 1, 0.36, 1)",
                    background: "linear-gradient(90deg, #eab308, #fde047)",
                  }}
                >
                  <div
                    className="ct-shimmer absolute inset-0"
                    style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)" }}
                  />
                </div>
                {TICKS.slice(1, -1).map((t) => (
                  <span
                    key={t}
                    className="absolute top-0 bottom-0 w-px bg-[var(--border-soft)]"
                    style={{ left: t * 100 + "%" }}
                  />
                ))}
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] text-[var(--text-tertiary)] tabular-nums">
                {TICKS.map((t) => (
                  <span key={t}>{fmt(Math.round(safeGoal * t))}</span>
                ))}
              </div>
            </div>

            {/* chips */}
            <div className="mt-6 flex flex-wrap gap-2">
              {["Free to enter", "$10,000 virtual balance", "Fresh start every month"].map((c) => (
                <span
                  key={c}
                  className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-secondary)] bg-[var(--bg-elevated)] border border-[var(--border-soft)] px-3 py-1.5"
                >
                  {c}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-yellow-400 text-black font-bold uppercase tracking-wide text-sm px-6 py-3 hover:bg-yellow-300 transition"
              >
                Join free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/trading-simulator"
                className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)] hover:text-yellow-400 transition"
              >
                Try the simulator →
              </Link>
            </div>
          </div>

          {/* RIGHT: podium */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-4 text-center lg:text-left">
              Monthly prize pool · 100 USDT
            </div>

            <div className="grid grid-cols-3 gap-3 items-end">
              {PRIZES.map((p) => (
                <div
                  key={p.rank}
                  className="relative flex flex-col items-center justify-end text-center px-2 pb-5 pt-4 bg-[var(--bg-elevated)]"
                  style={{
                    height: p.height + "px",
                    border: "1px solid " + p.color + "66",
                    boxShadow: p.rank === 1 ? "0 0 40px -10px rgba(250,204,21,0.45)" : "none",
                    background:
                      "linear-gradient(180deg, " + p.color + "1f 0%, transparent 70%), var(--bg-elevated)",
                  }}
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background: p.color }}
                  />
                  <Trophy size={p.rank === 1 ? 30 : 24} style={{ color: p.color }} />
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">
                    {p.label}
                  </div>
                  <div
                    className="mt-1 font-display font-bold leading-none tabular-nums"
                    style={{ color: p.color, fontSize: p.rank === 1 ? "40px" : "30px" }}
                  >
                    {p.amount}
                  </div>
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[var(--text-secondary)]">
                    USDT
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-5 text-xs text-[var(--text-tertiary)] text-center lg:text-left leading-relaxed">
              The pool grows to 100 / 60 / 40 USDT at 10,000 members. Free entry, rules apply.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}